import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Battery, LogIn, AlertCircle, Zap } from "lucide-react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4" data-testid="login-page">
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/661442fe-2c60-4494-bb2b-cd4283431309/images/f9396613ee0661711c3dd0b39ac0ea77d38339390843d1ee752c5835db3a8a51.png)`,
        }}
      />
      <div className="absolute inset-0 bg-cyber-bg/90 backdrop-blur-sm" />
      <div className="absolute inset-0 cyber-grid-bg opacity-50" />

      {/* Login Card */}
      <div className="relative w-full max-w-md" data-testid="login-card">
        <div className="bg-cyber-surface/90 backdrop-blur-xl border border-neon-blue/20 rounded-xl p-8 neon-glow-blue">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 bg-gradient-to-br from-neon-blue/20 to-neon-purple/20 border border-neon-blue/30 rounded-xl flex items-center justify-center neon-glow-blue">
              <Zap className="w-6 h-6 text-neon-blue" />
            </div>
            <div>
              <h1 className="font-heading font-black text-2xl tracking-tight text-white leading-none neon-text-blue">
                PROJET LX HEV
              </h1>
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-neon-blue/60">
                Gestion batteries hybrides
              </span>
            </div>
          </div>

          <h2 className="font-heading font-bold text-lg text-white mb-1">Connexion</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Entrez vos identifiants pour acceder au systeme
          </p>

          {error && (
            <div className="flex items-center gap-2 bg-neon-red-muted border border-neon-red/30 rounded-lg px-3 py-2.5 mb-4 neon-glow-red" data-testid="login-error">
              <AlertCircle className="w-4 h-4 text-neon-red shrink-0" />
              <span className="text-sm text-neon-red">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                required
                className="bg-cyber-bg/50 border-neon-blue/20 font-mono text-sm h-10 rounded-lg focus:ring-1 focus:ring-neon-blue focus:border-neon-blue"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                Mot de passe
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Votre mot de passe"
                required
                className="bg-cyber-bg/50 border-neon-blue/20 font-mono text-sm h-10 rounded-lg focus:ring-1 focus:ring-neon-blue focus:border-neon-blue"
                data-testid="login-password-input"
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox id="remember" data-testid="login-remember-checkbox" />
              <Label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer">
                Se souvenir de moi
              </Label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-neon-blue to-neon-purple text-white font-bold uppercase tracking-wide hover:from-neon-blue-hover hover:to-neon-purple h-10 rounded-lg neon-glow-blue"
              data-testid="login-submit-button"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Se connecter
                </>
              )}
            </Button>
          </form>

        </div>
      </div>
    </div>
  );
}
